import { useEffect, useMemo, useState } from "react";
import { SectionTitle } from "../_shared/section-title";
import { PageShell, Stack } from "../_shared/page-shell";
import { Card } from "../_shared/card";
import { CameraSheet } from "../_shared/camera-sheet";
import { ChildPicker } from "../_shared/child-picker";
import { PhotoGuide } from "../_shared/photo-guides";
import {
  IconArrowRight,
  IconCamera,
  IconCheck,
  IconClock,
} from "../_shared/icons";
import { children, type Child, type PhotoSession } from "../_data/mock";
import { loadPlanche, type Planche, type PlancheShot } from "../_data/planches";
import { useDemo } from "../_shared/demo.provider";
import { getChildFromUrl } from "../_shared/router";

type ActiveShot = { session: PhotoSession; shot: PlancheShot };

export function PhotoSessionsPage() {
  const {
    photoSessions,
    capturedShots,
    capturePhotoShot,
    completePhotoSession,
  } = useDemo();
  const [planches, setPlanches] = useState<Record<number, Planche>>({});
  const [active, setActive] = useState<ActiveShot | null>(null);
  const [selectedChildId, setSelectedChildId] = useState<Child["id"]>(() => {
    const fromUrl = getChildFromUrl();
    if (fromUrl && children.some((c) => c.id === fromUrl)) return fromUrl;
    return children[0]?.id ?? "jeanne";
  });

  useEffect(() => {
    let cancelled = false;
    const ids = Array.from(new Set(photoSessions.map((s) => s.plancheId)));
    Promise.all(ids.map((id) => loadPlanche(id).then((p) => [id, p] as const)))
      .then((entries) => {
        if (cancelled) return;
        const map: Record<number, Planche> = {};
        for (const [id, planche] of entries) map[id] = planche;
        setPlanches(map);
      })
      .catch((e) => {
        if (!cancelled) console.error("Échec chargement planches", e);
      });
    return () => {
      cancelled = true;
    };
  }, [photoSessions]);

  const onCapture = (dataUrl: string) => {
    if (!active) return;
    capturePhotoShot(
      active.session.id,
      String(active.shot.emplacement),
      dataUrl,
    );
    setActive(null);
  };

  const visible = useMemo(
    () => photoSessions.filter((s) => s.childId === selectedChildId),
    [photoSessions, selectedChildId],
  );
  const active_sessions = visible.filter((s) => s.status !== "done");
  const done_sessions = visible.filter((s) => s.status === "done");

  return (
    <>
      <SectionTitle title="Séances photos" />
      <PageShell>
        <Stack>
          <ChildPicker value={selectedChildId} onChange={setSelectedChildId} />
          {active_sessions.length === 0 ? (
            <Card title="Tout est à jour">
              <p className="text-[13px] text-[var(--text-2)]">
                Aucune séance en cours. Le cabinet vous notifiera dès qu'une
                nouvelle planche sera demandée.
              </p>
            </Card>
          ) : (
            active_sessions.map((session) => (
              <ActiveSessionCard
                key={session.id}
                session={session}
                planche={planches[session.plancheId]}
                capturedShots={capturedShots}
                onShotClick={(shot) => setActive({ session, shot })}
                onComplete={() => completePhotoSession(session.id)}
              />
            ))
          )}

          {done_sessions.length > 0 ? (
            <>
              <SectionDivider label="Historique" />
              {done_sessions.map((session) => (
                <DoneSessionCard
                  key={session.id}
                  session={session}
                  planche={planches[session.plancheId]}
                  capturedShots={capturedShots}
                />
              ))}
            </>
          ) : null}
        </Stack>
      </PageShell>

      <CameraSheet
        open={active !== null}
        onClose={() => setActive(null)}
        onCapture={onCapture}
        title={active ? active.shot.libelle : "Séance photo"}
        hint={active?.shot.instruction}
        orientation={active?.shot.orientation}
        width={active?.shot.width}
        height={active?.shot.height}
        flash={active?.shot.flash}
        miroirHorizontal={active?.shot.miroirHorizontal}
        miroirVertical={active?.shot.miroirVertical}
        guide={
          active ? (
            <PhotoGuide
              guideUrl={active.shot.guideUrl}
              orientation={active.shot.orientation}
            />
          ) : null
        }
      />
    </>
  );
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="mt-2 flex items-center gap-3 px-1">
      <h2 className="text-[13px] font-bold uppercase tracking-[0.1em] text-[var(--text-3)]">
        {label}
      </h2>
      <span className="h-px flex-1 bg-[var(--border-1)]" aria-hidden />
    </div>
  );
}

function ActiveSessionCard({
  session,
  planche,
  capturedShots,
  onShotClick,
  onComplete,
}: {
  session: PhotoSession;
  planche: Planche | undefined;
  capturedShots: Record<string, string>;
  onShotClick: (shot: PlancheShot) => void;
  onComplete: () => void;
}) {
  const total = planche?.photos.length ?? 0;
  const done = planche
    ? planche.photos.filter(
        (s) => capturedShots[`${session.id}::${s.emplacement}`],
      ).length
    : 0;
  const allDone = total > 0 && done >= total;

  return (
    <Card title={session.label} multilineTitle>
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-1.5 text-[13px] text-[var(--text-2)]">
          <IconClock width={14} height={14} className="text-[var(--text-3)]" />
          Pour le {session.dueDate}
        </p>
        <span
          className={`inline-flex h-6 items-center rounded-full px-2.5 text-[11.5px] font-semibold ${
            allDone
              ? "bg-[var(--sf-primary-100)] text-[var(--sf-primary)]"
              : "bg-[var(--surface-2)] text-[var(--text-2)]"
          }`}
        >
          {done}/{total || "…"} clichés
        </span>
      </div>

      {planche ? (
        <p className="mt-1.5 text-[12px] text-[var(--text-3)]">
          Planche {planche.id} · {planche.libelle}
        </p>
      ) : null}

      <ul className="mt-4 grid grid-cols-3 gap-2.5 sm:grid-cols-4">
        {(planche?.photos ?? []).map((shot) => {
          const key = `${session.id}::${shot.emplacement}`;
          const captured = capturedShots[key];
          return (
            <li key={shot.emplacement}>
              <button
                type="button"
                onClick={() => onShotClick(shot)}
                className={`group relative flex w-full ${
                  shot.orientation === "portrait"
                    ? "aspect-[3/4]"
                    : "aspect-[4/3]"
                } flex-col items-center justify-center overflow-hidden rounded-2xl bg-[var(--surface-2)] ring-1 ring-[var(--border-1)] transition active:scale-[0.97]`}
              >
                {captured ? (
                  <>
                    <img
                      src={captured}
                      alt={shot.libelle}
                      className="absolute inset-0 size-full object-cover"
                    />
                    <span className="absolute right-1.5 top-1.5 inline-flex size-6 items-center justify-center rounded-full bg-[var(--sf-primary)] text-white shadow-md">
                      <IconCheck width={14} height={14} strokeWidth={2.5} />
                    </span>
                  </>
                ) : (
                  <img
                    src={shot.remplacementUrl}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 size-full object-contain opacity-40"
                  />
                )}
                <span
                  className={`absolute inset-x-1 bottom-1 truncate rounded-md px-1.5 py-0.5 text-center text-[10.5px] font-semibold ${
                    captured
                      ? "bg-black/45 text-white backdrop-blur"
                      : "text-[var(--text-2)]"
                  }`}
                >
                  {shot.libelle}
                </span>
                {!captured ? (
                  <span className="absolute right-1.5 top-1.5 inline-flex size-6 items-center justify-center rounded-full bg-[var(--surface-1)] text-[var(--text-3)] ring-1 ring-[var(--border-1)]">
                    <IconCamera width={12} height={12} />
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>

      {planche ? (
        <button
          type="button"
          disabled={!allDone}
          onClick={onComplete}
          className="mt-4 inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-[var(--sf-primary)] px-5 text-[14px] font-semibold text-white shadow-sm transition active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-[var(--surface-3)] disabled:text-[var(--text-3)] sm:mx-auto sm:max-w-sm"
        >
          {allDone ? (
            <>
              Envoyer la séance
              <IconArrowRight width={14} height={14} />
            </>
          ) : (
            <>
              {total - done} cliché{total - done > 1 ? "s" : ""} restant
              {total - done > 1 ? "s" : ""}
            </>
          )}
        </button>
      ) : null}
    </Card>
  );
}

function DoneSessionCard({
  session,
  planche,
  capturedShots,
}: {
  session: PhotoSession;
  planche: Planche | undefined;
  capturedShots: Record<string, string>;
}) {
  return (
    <Card title={session.label} multilineTitle>
      <div className="flex items-center justify-between gap-3">
        <p className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--sf-primary)]">
          <IconCheck width={14} height={14} strokeWidth={2.5} />
          Envoyée au cabinet
        </p>
        {planche ? (
          <span className="text-[11.5px] text-[var(--text-3)]">
            {planche.photos.length} clichés
          </span>
        ) : null}
      </div>

      {planche ? (
        <ul className="mt-3 grid grid-cols-6 gap-1.5 sm:grid-cols-8">
          {planche.photos.map((shot) => {
            const key = `${session.id}::${shot.emplacement}`;
            const captured = capturedShots[key];
            return (
              <li
                key={shot.emplacement}
                className="aspect-square overflow-hidden rounded-md bg-[var(--surface-2)] ring-1 ring-[var(--border-1)]"
              >
                {captured ? (
                  <img
                    src={captured}
                    alt={shot.libelle}
                    className="size-full object-cover"
                  />
                ) : (
                  <img
                    src={shot.remplacementUrl}
                    alt=""
                    aria-hidden
                    className="size-full object-contain opacity-50"
                  />
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </Card>
  );
}
